import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import {
  apiRequest,
  formatCurrency,
  getProfileImage,
} from "../../../core/common/api";
import { canManageUsers } from "../../../core/common/adminPermissions";
import MetricCard from "../../../shared/MetricCard";
import AppImage from "../../../shared/AppImage";
import PaginationBar from "../../../shared/PaginationBar";
import ResetButton from "../../../shared/ResetButton";
import useTableSearch from "../../../shared/useTableSearch";
import { clubLabel } from "../../../shared/Utils";

const AllUser = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const latestRequest = useRef(0);
  const [users, setUsers] = useState<any[]>([]);
  const [stats, setStats] = useState<any>({
    totalUsers: 0,
    totalEWallet: 0,
    totalRepurchaseFund: 0,
    totalWorkingFund: 0,
    totalUplineFund: 0,
    totalUpgradeFund: 0,
    totalProfitFund: 0,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    totalPages: 1,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: searchParams.get("search") || "",
    rank: "all",
    isActive: "all",
  });
  const searchTerm = useTableSearch(filters.search);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchUsers = useCallback(
    async (page = 1) => {
      const requestId = ++latestRequest.current;
      setIsLoading(true);
      try {
        const params = new URLSearchParams({
          search: searchTerm,
          page: String(page),
          limit: String(pagination.limit),
        });

        if (filters.rank !== "all") params.append("rank", filters.rank);
        if (filters.isActive !== "all") params.append("isActive", filters.isActive);
        const res = await apiRequest(`/users?${params.toString()}`, "GET");
        if (requestId !== latestRequest.current) return;
        if (res.success) {
          setUsers(res.users || []);
          setStats(res.stats || {});
          setPagination({
            page: res.pagination?.page || page,
            limit: res.pagination?.limit || pagination.limit,
            totalPages: res.pagination?.totalPages || 1,
            total:
              res.pagination?.total ??
              res.totalUsers ??
              (res.users || []).length,
          });
        }
      } catch (error: any) {
        if (requestId !== latestRequest.current) return;
        setUsers([]);
        Swal.fire(
          "Error Loading Users",
          error.message || "Failed to load user list",
          "error",
        );
      } finally {
        if (requestId === latestRequest.current) setIsLoading(false);
      }
    },
    [filters.rank, filters.isActive, searchTerm, pagination.limit],
  );

  useEffect(() => {
    fetchUsers(1);
  }, [fetchUsers]);

  const metrics = [
    {
      label: "Total Users",
      value: stats.totalUsers || users.length,
      icon: "👥",
      tone: "blue",
    },
    {
      label: "Total eWallet",
      value: formatCurrency(stats.totalEWallet || 0),
      icon: "💳",
      tone: "green",
    },
    {
      label: "Total Re-purchases",
      value: formatCurrency(stats.totalRepurchaseFund || 0),
      icon: "🔁",
      tone: "orange",
    },
    {
      label: "Total Working Fund",
      value: formatCurrency(stats.totalWorkingFund || 0),
      icon: "⚡",
      tone: "green",
    },
    {
      label: "Total Up-line Fund",
      value: formatCurrency(stats.totalUplineFund || 0),
      icon: "⬆️",
      tone: "blue",
    },
    {
      label: "Total Upgrade Fund",
      value: formatCurrency(stats.totalUpgradeFund || 0),
      icon: "🔒",
      tone: "blue",
    },
    {
      label: "Total Profit Fund",
      value: formatCurrency(stats.totalProfitFund || 0),
      icon: "🏦",
      tone: "green",
    },
  ];

  return (
    <>
      <div className="page-hdr">
        <div className="page-hdr-left">
          <div className="page-eyebrow">User Management</div>
          <h1 className="page-title">
            All <em>Users</em>
          </h1>
          <div className="page-sub">
            Manage customer accounts, roles, access permissions and credentials.
          </div>
        </div>

        <div className="page-hdr-right">
          {canManageUsers() && (
            <>
              <Link to="/add-users" className="btn-sm btn-primary">
                Add User
              </Link>
              <Link to="/add-sale-users" className="btn-sm btn-outline">
                Add Sale User
              </Link>
            </>
          )}
        </div>
      </div>

      <section className="metrics-grid">
        {metrics.map((metric) => (
          <MetricCard key={metric.label} metric={metric} />
        ))}
      </section>

      <div className="toolbar">
        <div className="toolbar-search">
          <svg viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            placeholder="Search by name, email, phone, member ID..."
          />
        </div>

        <select className="filter-select" aria-label="Rank" value={filters.rank}
          onChange={e => setFilters({ ...filters, rank: e.target.value })}>
          <option value="all">All ranks</option>
          <option value="none">No rank</option>
          {["Leader", "Director", "Partner", "Royalty"].map(rank => <option key={rank} value={rank}>{rank}</option>)}
        </select>

        <select
          className="filter-select"
          aria-label="Status"
          value={filters.isActive}
          onChange={(e) => setFilters({ ...filters, isActive: e.target.value })}
        >
          <option value="all">All statuses</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
        <ResetButton
          onClick={() =>
            setFilters({
              search: "",
              rank: "all",
              isActive: "all",
            })
          }
        />
      </div>

      <section className="table-card">
        <div className="table-head">
          <h2>
            Registered Users (
            {pagination.total})
          </h2>
          <p>List of all customers and staff members</p>
        </div>

        <div className="table-scroll">
          <table className="users-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Phone</th>
                <th>Member ID</th>
                <th>Status</th>
                <th>Club</th>
                <th>Rank</th>
                <th>Sponsor</th>
                <th>eWallet</th>
                <th>Repurchases</th>
                <th>Working</th>
                <th>Upline</th>
                <th>Upgrade</th>
                <th>Profit</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={14} className="empty-state">
                    Loading Users...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={14} className="empty-state">
                    No Users found matching your search.
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user._id}>
                    <td>
                      <div className="img-name-cell">
                        <AppImage
                          src={getProfileImage(user)}
                          alt={user.name}
                          crossOrigin="anonymous"
                        />
                        <div>
                          <strong>{user.name || "Customer"}</strong>
                        </div>
                      </div>
                    </td>
                    
                    <td>{user.phone || "-"}</td>
                    <td>
                      <strong>{user.memberId || "—"}</strong>
                    </td>
                    <td>
                      <span
                        className={`status-badge ${
                          user.isActive ? "success" : "warning"
                        }`}
                      >
                        {user.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>{user.club != null ? clubLabel(user.club) : "—"}</td>
                    <td>{user.rank || "No rank"}</td>
                    <td>{user.sponsorId || "—"}</td>
                    <td>
                      <strong>{formatCurrency(user.eWallet ?? 0)}</strong>
                    </td>
                    <td>
                      <strong>{formatCurrency(user.repurchaseFund ?? 0)}</strong>
                    </td>
                    <td>
                      <strong>{formatCurrency(user.workingFund ?? 0)}</strong>
                    </td>
                    <td>
                      <strong>{formatCurrency(user.uplineFund ?? 0)}</strong>
                    </td>
                    <td>
                      <strong>{formatCurrency(user.upgradeFund ?? 0)}</strong>
                    </td>
                    <td>
                      <strong>{formatCurrency(user.profitFund ?? 0)}</strong>
                    </td>
                    
                    <td>
                      <div className="actions-btns order-actions">
                        <button
                          type="button"
                          onClick={() => navigate(`/users/${user._id}`)}
                          title="View User Details"
                          className="action-button edit"
                        >
                          👁️
                        </button>
                        {canManageUsers() && (
                          <button
                            type="button"
                            onClick={() =>
                              navigate(`/users/${user._id}?edit=1`)
                            }
                            title="Edit User"
                            className="action-button edit"
                          >
                            ✏️
                          </button>
                        )}

                        
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <PaginationBar
          page={pagination.page}
          limit={pagination.limit}
          total={pagination.total}
          totalPages={pagination.totalPages}
          itemLabel="users"
          pageSizeOptions={[100, 200, 500, 1000, 2000, 3000]}
          onPageChange={fetchUsers}
          onLimitChange={(limit) =>
            setPagination((current) => ({ ...current, page: 1, limit }))
          }
        />
      </section>
    </>
  );
};

export default AllUser;
